import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { getHeaders } from 'src/app/shared/utils/headers.functions';

const apiUrl = environment.API_URL;
const DISMISSED_KEY = 'lp_dismissed';

export interface LaunchpadSteps {
  account:  boolean;   // Mi cuenta: bio completa
  brand:    boolean;   // Identidad de marca: bannerUrl presente
  team:     boolean;   // Mi equipo: ≥1 miembro activo
  products: boolean;   // Mis productos: ≥1 producto creado
  payments: boolean;   // Métodos de cobro: openpayid configurado
}

export interface LaunchpadStatus {
  steps: LaunchpadSteps;
  completedCount: number;
  total: number;
  allDone: boolean;
}

const EMPTY_STATUS: LaunchpadStatus = {
  steps:          { account: false, brand: false, team: false, products: false, payments: false },
  completedCount: 0,
  total:          5,
  allDone:        false,
};

@Injectable({ providedIn: 'root' })
export class LaunchpadService {

  private statusSubject = new BehaviorSubject<LaunchpadStatus>(EMPTY_STATUS);
  /** Observable del estado del launchpad — úsalo en sidebar y componente */
  status$: Observable<LaunchpadStatus> = this.statusSubject.asObservable();

  constructor(private http: HttpClient) {}

  private get headers(): HttpHeaders { return getHeaders(); }

  // ── Carga el estado desde el API ──────────────────────────────────────────

  load(): Observable<LaunchpadStatus> {
    return this.http
      .get<LaunchpadStatus>(`${apiUrl}/launchpad/check`, { headers: this.headers })
      .pipe(
        tap((status) => this.statusSubject.next(status)),
        catchError(() => {
          // API no disponible todavía: devolver estado vacío sin bloquear
          return of(EMPTY_STATUS);
        }),
      );
  }

  get currentStatus(): LaunchpadStatus {
    return this.statusSubject.value;
  }

  // ── Dismissed (Launchpad oculto cuando todos los pasos están completos) ───

  get isDismissed(): boolean {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  }

  dismiss(): void {
    localStorage.setItem(DISMISSED_KEY, 'true');
    // También persiste en profile_extra via el servicio existente
    // (se hace en el componente para no crear circular dep aquí)
  }

  /** Resetea el estado dismissed (útil para dev / re-onboarding) */
  resetDismissed(): void {
    localStorage.removeItem(DISMISSED_KEY);
  }

  /** true si el Launchpad debe mostrarse en el sidebar */
  get shouldShowInSidebar(): boolean {
    return !this.isDismissed && !this.currentStatus.allDone;
  }
}
